/**
 * Chargement progressif des grandes tailles de page.
 *
 * Demander 500, 1000 ou « Tout afficher » lignes en une requête, c'est plusieurs
 * Mo de JSON, un pic mémoire côté serveur et un onglet figé le temps de peindre
 * le résultat. Dès que la taille de page dépasse `PROGRESSIVE_CHUNK_SIZE`, la
 * page est donc servie en plusieurs tranches : la première au chargement, les
 * suivantes quand le bas de la liste approche du viewport (voir `ListLoadMore`).
 *
 * La pagination classique reste intacte : une page de 500 affiche bien 500
 * lignes une fois remplie, et la navigation entre pages ne change pas. Les
 * tailles inférieures à la tranche (10, 50, 100) sont servies d'un bloc, comme
 * avant — `begin()` renvoie alors exactement la fenêtre `limit`/`offset`
 * d'aujourd'hui.
 *
 * Deux usages :
 * - listes paginées côté serveur → passer `items` et `loadPage` ;
 * - listes déjà en mémoire (pagination côté client) → passer `page` et découper
 *   la liste avec `loadedCount`.
 *
 * @example
 * // Liste paginée côté serveur
 * const progressive = useProgressiveList({ pageSize: limit, items: formateurs, total, loadPage: (p) => load(p) })
 *
 * async function load(page = 1) {
 *   const chunk = progressive.begin(page)
 *   // Une tranche s'ajoute en bas de liste : `loading` reste faux, sinon le
 *   // tableau déjà affiché serait escamoté à chaque défilement.
 *   loading.value = !chunk.append
 *   const data = await api.get(endpoints.formateurs.list({ page, limit: chunk.limit }))
 *   if (!chunk.apply(data.formateurs || [])) return   // requête périmée
 *   total.value = data.total || 0
 * }
 */

import { ref, computed, reactive } from 'vue'
import { ALL_PAGE_SIZE, PROGRESSIVE_CHUNK_SIZE } from '@shared/list-page-sizes.js'

/**
 * @param {Object} options
 * @param {import('vue').Ref<number>} options.pageSize - Taille de page choisie par l'utilisateur.
 * @param {import('vue').Ref<number>} options.total - Total renvoyé par le serveur (ou taille de la liste filtrée).
 * @param {import('vue').Ref<Array>} [options.items] - Liste affichée, remplacée ou complétée par `apply()`.
 * @param {(page:number)=>Promise<any>} [options.loadPage] - Recharge la page courante ; absent = pagination côté client.
 * @param {import('vue').Ref<number>} [options.page] - Page courante d'une liste paginée côté client.
 * @param {number} [options.maxItems] - Plafond de lignes servies pour une même page.
 */
export function useProgressiveList({
  pageSize,
  total,
  items = null,
  loadPage = null,
  page = null,
  maxItems = ALL_PAGE_SIZE,
}) {
  const clientMode = loadPage === null

  const size = computed(() => Number(pageSize.value))
  /** Une page plus grande qu'une tranche est servie en plusieurs fois. */
  const isProgressive = computed(() => size.value > PROGRESSIVE_CHUNK_SIZE)
  const chunkSize = computed(() => Math.min(PROGRESSIVE_CHUNK_SIZE, size.value))

  // Serveur : lignes reçues pour la page courante. Écrit par `apply()` seulement,
  // pour que le compteur affiché ne retombe pas à zéro pendant un rechargement.
  const fetchedInPage = ref(0)
  // Client : la liste est déjà en mémoire, seule la fenêtre rendue grandit.
  const revealedChunks = ref(1)

  // Dernière page effectivement servie — pas la page demandée : tant qu'une
  // requête est en vol, les compteurs continuent de décrire ce qui est à l'écran.
  const loadedPage = ref(1)
  const exhausted = ref(false)

  // Incrémenté chaque fois que le contenu affiché change. `ListLoadMore` s'en
  // sert pour réarmer son observation : un IntersectionObserver ne renotifie
  // que sur un changement d'état, or après un changement de page la sentinelle
  // reste montée et déjà visible — sans réarmement, elle ne rappellerait jamais.
  const revision = ref(0)
  const loadingMore = ref(false)

  // Une requête qui remplace la liste est partie : les compteurs décrivent
  // encore l'affichage précédent, réclamer « la suite » raccorderait une tranche
  // à une liste sur le point de disparaître.
  const pendingReplace = ref(false)

  // Une tranche n'est pas arrivée (réseau, erreur serveur) : la sentinelle
  // arrête de relancer toute seule, sinon elle martèle la même requête. Le
  // bouton « Charger la suite » reste, lui, disponible.
  const autoPaused = ref(false)

  // Invalide les requêtes encore en vol quand une nouvelle est lancée : sans ça
  // une tranche partie avant un changement de filtre s'ajoute à la liste refaite.
  let generation = 0
  // Positionné par `loadMore()` et consommé par le `begin()` qui suit : c'est ce
  // qui distingue « la suite de la page courante » d'un rechargement de page.
  let continuing = false

  const currentPage = computed(() => (clientMode && page ? Number(page.value) : loadedPage.value))
  const pageStart = computed(() => (currentPage.value - 1) * size.value)

  /** Lignes réellement disponibles pour la page courante. */
  const pageCapacity = computed(() =>
    Math.min(size.value, maxItems, Math.max(0, total.value - pageStart.value))
  )

  const loadedInPage = computed(() =>
    clientMode
      ? Math.min(revealedChunks.value * chunkSize.value, pageCapacity.value)
      : fetchedInPage.value
  )

  /** Lignes chargées pour la page courante, ou `null` si la page arrive d'un bloc. */
  const loadedCount = computed(() => (isProgressive.value ? loadedInPage.value : null))

  /** Reste-t-il des lignes à charger sur cette page ? */
  const hasMore = computed(() =>
    isProgressive.value &&
    !exhausted.value &&
    !pendingReplace.value &&
    loadedInPage.value < pageCapacity.value
  )

  /** Taille de la prochaine tranche : la dernière d'une page est plus courte. */
  const nextChunkSize = computed(() =>
    Math.max(0, Math.min(chunkSize.value, pageCapacity.value - loadedInPage.value))
  )

  /** Le plafond `maxItems` masque-t-il une partie de la page ? */
  const capped = computed(() =>
    isProgressive.value && Math.min(size.value, total.value - pageStart.value) > maxItems
  )

  /**
   * Repart de la première tranche et périme les requêtes en vol.
   * À appeler quand la liste change de contenu sans repasser par `begin()`
   * (filtres ou changement de page d'une liste paginée côté client).
   */
  function reset() {
    generation++
    revision.value++
    loadedPage.value = 1
    fetchedInPage.value = 0
    revealedChunks.value = 1
    exhausted.value = false
    pendingReplace.value = false
    autoPaused.value = false
    continuing = false
  }

  /**
   * Ouvre une requête de liste : donne la fenêtre à demander au serveur et la
   * fonction qui range le résultat (remplacement, ou ajout si `loadMore()` vient
   * de réclamer la suite de la page courante).
   *
   * @param {number} [requestedPage] - Page demandée.
   * @returns {{ limit:number, offset:number, append:boolean, stale:boolean, apply:(rows:Array, receivedCount?:number)=>boolean }}
   */
  function begin(requestedPage = 1) {
    const gen = ++generation
    const append = continuing && isProgressive.value && requestedPage === loadedPage.value
    continuing = false
    if (!append) pendingReplace.value = true

    const alreadyLoaded = append ? fetchedInPage.value : 0
    const limit = Math.min(chunkSize.value, size.value - alreadyLoaded)

    return {
      limit,
      offset: (requestedPage - 1) * size.value + alreadyLoaded,
      append,
      /** Une requête plus récente a été lancée depuis : ce résultat est à jeter. */
      get stale() {
        return gen !== generation
      },
      /**
       * Range la tranche reçue dans `items`.
       *
       * @param {Array} rows - Lignes à afficher (déjà filtrées côté client le cas échéant).
       * @param {number} [receivedCount] - Lignes renvoyées par le serveur avant filtrage client.
       * @returns {boolean} `false` si la requête est périmée : l'appelant doit s'arrêter là.
       */
      apply(rows, receivedCount = rows.length) {
        if (gen !== generation) return false

        if (items) items.value = append ? items.value.concat(rows) : rows
        revision.value++
        loadedPage.value = requestedPage
        fetchedInPage.value = alreadyLoaded + receivedCount
        pendingReplace.value = false
        // Une tranche incomplète signe la fin des données, même si `total` dit le
        // contraire (filtres appliqués après le comptage, données modifiées
        // entre deux tranches).
        exhausted.value = receivedCount < limit

        return true
      },
    }
  }

  /**
   * Charge la tranche suivante de la page courante.
   * @param {{ auto?: boolean }} [options] - `auto` pour un appel de la sentinelle,
   *   qui reste en retrait tant qu'une tranche n'est pas revenue.
   */
  async function loadMore({ auto = false } = {}) {
    if (auto && autoPaused.value) return
    if (!hasMore.value || loadingMore.value) return

    // `revision` sert de signal de vie : elle bouge dès qu'un résultat a été
    // rangé, que ce soit notre tranche ou le rechargement qui l'a supplantée.
    // La comparer aux lignes chargées prendrait une tranche périmée par un
    // changement de page pour un échec, et couperait le chargement automatique
    // pour le reste de la session.
    const revisionBefore = revision.value
    loadingMore.value = true
    try {
      if (clientMode) {
        revealedChunks.value += 1
        revision.value++
      } else {
        continuing = true
        await loadPage(currentPage.value)
      }
    } finally {
      continuing = false
      autoPaused.value = revision.value === revisionBefore
      loadingMore.value = false
    }
  }

  // `reactive` et non un objet de refs : l'état voyage d'un bloc à l'autre en un
  // seul objet (`progressive.hasMore` lisible tel quel dans les templates).
  // Corollaire : ne pas le déstructurer, ça casserait la réactivité.
  return reactive({
    isProgressive,
    revision,
    nextChunkSize,
    loadedCount,
    hasMore,
    capped,
    loadingMore,
    begin,
    loadMore,
    reset,
  })
}
